import { claimOncePerPageView, currentPath, getAnalyticsLocale } from "./page";
import { getOrCreateSessionUUID, getStoredContext } from "./session";
import { hasTrackingConsent } from "./consent";

/** Transport options of an event. */
export type TrackEventOptions = {
  /**
   * The click also leaves the page (link to the app or a store): send with
   * `navigator.sendBeacon` (or a keepalive fetch) so the request survives the navigation.
   */
  criticalOnNav?: boolean;
};

type ObsPayload = {
  uID: string;
  context: string | null;
  action: string;
  metadata: Record<string, string>;
};

/** Where a CTA leads, sent as `target` with `site-cta-click`. */
export type CtaTarget = "web" | "ios" | "android" | "demo" | "contact";

function compact(metadata: Record<string, string | undefined>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(metadata)) {
    if (value !== undefined && value !== "") out[key] = value;
  }
  return out;
}

async function sendObsEvent(payload: ObsPayload, options: TrackEventOptions): Promise<void> {
  try {
    const body = JSON.stringify(payload);
    if (options.criticalOnNav && typeof navigator !== "undefined" && navigator.sendBeacon) {
      const queued = navigator.sendBeacon(
        "/api/obs/rec",
        new Blob([body], { type: "application/json" }),
      );
      if (queued) return;
    }

    await fetch("/api/obs/rec", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: Boolean(options.criticalOnNav),
    });
  } catch (error) {
    // Observability must never break the page.
    console.warn("[obs] Failed to send event:", error);
  }
}

/**
 * Sends an observability event for the consented website session. Does nothing without
 * consent. Every event carries the current `path` and `locale` unless the caller sets them.
 *
 * @param action - Event name (`site-*` for the analytics contract, legacy names otherwise).
 * @param metadata - String metadata; empty values are dropped.
 * @param options - Transport options.
 */
export async function trackEvent(
  action: string,
  metadata: Record<string, string | undefined> = {},
  options: TrackEventOptions = {},
): Promise<void> {
  const uuid = getOrCreateSessionUUID();
  if (!uuid) return;

  await sendObsEvent(
    {
      uID: uuid,
      context: getStoredContext(),
      action,
      metadata: compact({ path: currentPath(), locale: getAnalyticsLocale(), ...metadata }),
    },
    options,
  );
}

/**
 * Sends `site-cta-click` for a CTA that opens the app, a store, the demo or the contact form.
 * Always uses the navigation-safe transport, since the click usually leaves the page.
 *
 * @param placement - Kebab-case CTA id (`hero`, `navbar`, `pricing-pro`, `footer`…).
 * @param target - Where the CTA leads.
 * @param extra - Optional `profile` or `plan`.
 */
export function trackCtaClick(
  placement: string,
  target: CtaTarget,
  extra: { profile?: string; plan?: string } = {},
): void {
  void trackEvent("site-cta-click", { placement, target, ...extra }, { criticalOnNav: true });
}

/**
 * Sends an event at most once per page view for a given key (section seen, depth reached,
 * story step shown). Nothing is claimed without consent, so the event can still fire once the
 * visitor accepts cookies.
 *
 * @param key - Deduplication key within the page view.
 * @param action - Event name.
 * @param metadata - Event metadata.
 */
export function trackOncePerPageView(
  key: string,
  action: string,
  metadata: Record<string, string>,
): void {
  if (!hasTrackingConsent()) return;
  if (!claimOncePerPageView(key)) return;
  void trackEvent(action, metadata);
}

/**
 * Sends `site-story-step` the first time a home story step becomes active in this page view.
 *
 * @param step - Step id (`problem`, `build`, `action`).
 * @param index - Zero-based position of the step.
 */
export function trackStoryStep(step: string, index: number): void {
  trackOncePerPageView(`story:${step}`, "site-story-step", { step, index: String(index) });
}
